// WP-2.1 (S-42/43 per §18.3's Settings list, SE-4/WB-2/WB-4): the Settings→WhatsApp page —
// bridge status, designated chat, re-link QR flow, and the authorized-numbers allowlist.
// Note: DEV_PLAN cites this screen as "S-43" but §18.3's combined S-39..46 entry has an
// off-by-one (nine groups, eight ids) — surfaced as an ambiguity, not resolved here.
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { WhatsAppLinkPanel } from '../../components/WhatsAppLinkPanel';
import { inputClass, primaryButtonInlineClass } from '../../components/FormKit';
import { useSettings, useSetSetting } from '../../lib/settings';

export function SettingsWhatsApp(): React.JSX.Element {
  const { data: settings } = useSettings();
  const setSetting = useSetSetting();
  const [draft, setDraft] = useState('');

  const allowedRaw = settings?.whatsappAllowedNumbers;
  const allowed = Array.isArray(allowedRaw) ? allowedRaw.map(String) : [];
  const designated =
    typeof settings?.whatsappDesignatedChat === 'string'
      ? settings.whatsappDesignatedChat
      : undefined;

  const addNumber = (): void => {
    const digits = draft.replace(/\D/g, '');
    if (digits === '' || allowed.includes(digits)) return;
    setSetting.mutate({ key: 'whatsappAllowedNumbers', value: [...allowed, digits] });
    setDraft('');
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium text-ink">Bridge</h2>
        <WhatsAppLinkPanel />
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Designated chat</h2>
        <p className="mb-2 text-xs text-muted">
          Only messages in this chat are processed (WB-2). It's adopted automatically the first time
          an authorized number messages the assistant.
        </p>
        <p className="text-sm text-ink">
          {designated ?? 'Not set yet — message the assistant to adopt it.'}
        </p>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Authorized numbers</h2>
        <p className="mb-3 text-xs text-muted">
          Only these numbers can write into your books (SE-4). Everyone else gets one polite refusal
          and is logged.
        </p>
        <ul className="mb-3 space-y-1">
          {allowed.length === 0 && (
            <li className="text-sm text-danger">Empty — nothing will be accepted.</li>
          )}
          {allowed.map((n) => (
            <li key={n} className="flex items-center justify-between text-sm text-ink">
              <span>{n}</span>
              <button
                type="button"
                aria-label={`Remove ${n}`}
                className="rounded p-1 text-muted hover:text-danger"
                disabled={setSetting.isPending}
                onClick={() => {
                  setSetting.mutate({
                    key: 'whatsappAllowedNumbers',
                    value: allowed.filter((x) => x !== n),
                  });
                }}
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input
            className={`${inputClass} flex-1`}
            aria-label="Add authorized number"
            placeholder="e.g. 923001234567 (digits only)"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addNumber();
            }}
          />
          <button
            type="button"
            className={primaryButtonInlineClass}
            disabled={setSetting.isPending}
            onClick={addNumber}
          >
            <span className="flex items-center gap-1">
              <Plus size={14} /> Add
            </span>
          </button>
        </div>
      </section>
    </div>
  );
}
